import {
  Box,
  CircularProgress,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Forbidden } from '../../shared/components/SconApp/forbidden/Forbidden';
import {
  BusinessTabsEnum,
  BUSINESS_TAB_CONFIG,
  ALL_BUSINESS_TABS,
  RECEPTIONIST_TABS,
  FEATURE_TAB_GATES,
} from '../../shared/constants/businessTabs';
import {
  UserRoleEnum,
  useAppThemeContext,
  useBusinessContext,
  useUserContext,
} from '../../shared/contexts';
import { useTabNavigation } from '../../shared/hooks/useTabNavigation';
import { LayoutShell, LayoutRegister } from '../../shared/layouts';
import {
  IBusinessView,
  businessService,
  businessAdminService,
  employeeService,
} from '../../shared/services';
import {
  BusinessAdmins,
  BusinessOrderTickets,
  BusinessTransactions,
  BusinessCommissions,
  BusinessDashboard,
  BusinessInformation,
  BusinessProducts,
  BusinessEmployees,
  BusinessServices,
  BusinessCaixa,
  BusinessCashFlow,
  BusinessClients,
  BusinessWhatsApp,
  BusinessPackages,
  BusinessGoals,
  BusinessFinancialSettings,
  BusinessCampaigns,
} from './pages';
import { MyPlan } from '../myPlan/MyPlan';
import { OnboardingBanner } from './OnboardingBanner';

export interface IPropsBusinessPage {
  business: IBusinessView;
  refetch: () => void;
}

const BlockedScreen: React.FC = () => (
  <LayoutShell>
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      minHeight='60vh'
      gap={3}
      textAlign='center'
      px={2}
    >
      <Icon sx={{ fontSize: 80, color: 'error.main' }}>block</Icon>
      <Typography variant='h5' fontWeight={600} color='error.main'>
        Acesso bloqueado
      </Typography>
      <Typography variant='body1' color='text.secondary' maxWidth={400}>
        Seu acesso está bloqueado, entre em contato com o administrador do SconApp.
      </Typography>
    </Box>
  </LayoutShell>
);

export const Business: React.FC = () => {
  const { userId, userRoles, isAuthenticated } = useUserContext();
  const { page } = useBusinessContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isReceptionist = userRoles.includes(UserRoleEnum.Receptionist);

  const { lgDown } = useAppThemeContext();

  // Admin: carrega businesses via BusinessAdmin
  const { data: businessAdmins, isFetching: isLoadingBusinessAdmin } = useQuery(
    ['businessAdmin'],
    () => businessAdminService.getByUserId(userId),
    { enabled: userRoles.includes(UserRoleEnum.BusinessAdmin) }
  );

  // Receptionist: carrega businesses via Employee → BusinessEmployees
  const { data: employee, isFetching: isLoadingEmployee } = useQuery(
    ['employee'],
    () => employeeService.getByUserId(userId),
    { enabled: isReceptionist }
  );

  const [selectedBusinessAdminId, setSelectedBusinessAdminId] = useState<number | null>(null);
  const [selectedBizEmpId, setSelectedBizEmpId] = useState<number | null>(null);

  // Resolve o businessAdmin selecionado (admin flow)
  const businessAdmin = useMemo(() => {
    if (!businessAdmins?.length) return undefined;
    if (selectedBusinessAdminId) {
      return businessAdmins.find(a => a.id === selectedBusinessAdminId) ?? businessAdmins[0];
    }
    return businessAdmins[0];
  }, [businessAdmins, selectedBusinessAdminId]);

  // Resolve o businessEmployee selecionado (receptionist flow)
  const selectedBizEmp = useMemo(() => {
    if (!employee?.businessEmployees?.length) return undefined;
    if (selectedBizEmpId) {
      return employee.businessEmployees.find(be => be.id === selectedBizEmpId) ?? employee.businessEmployees[0];
    }
    return employee.businessEmployees[0];
  }, [employee, selectedBizEmpId]);

  // businessId de qualquer fonte
  const businessId = useMemo(() => {
    return businessAdmin?.businessId ?? selectedBizEmp?.businessId;
  }, [businessAdmin, selectedBizEmp]);

  const {
    data: business,
    refetch: refetchBusiness,
    isFetching: isLoadingBusiness,
  } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => businessId ? businessService.getById(businessId) : undefined,
    enabled: !!businessId,
  });

  // Tabs to hide when their feature is disabled in the current plan
  const hiddenTabs = useMemo(() => {
    if (!business?.subscription?.features?.length) return new Set<number>();

    const features = business.subscription.features;
    const hidden = new Set<number>();

    for (const [featureCode, tabs] of Object.entries(FEATURE_TAB_GATES)) {
      const feature = features.find(f => f.code === featureCode);
      if (!feature?.enabled) {
        tabs.forEach(t => hidden.add(t));
      }
    }

    return hidden;
  }, [business?.subscription?.features]);

  // renderedTabs: tabs que serão efetivamente exibidas, na ordem correta
  const renderedTabs = useMemo(() => {
    return ALL_BUSINESS_TABS.filter(t => {
      if (isReceptionist && !RECEPTIONIST_TABS.has(t)) return false;
      if (hiddenTabs.has(t)) return false;
      return true;
    });
  }, [isReceptionist, hiddenTabs]);

  const { tabIndex, visualIndex, onChangeTab: onVisualTabChange, setTabIndex } = useTabNavigation(renderedTabs, BusinessTabsEnum.Caixa);

  // If current tab is now hidden, reset to Caixa
  useEffect(() => {
    if (hiddenTabs.has(tabIndex)) {
      setTabIndex(BusinessTabsEnum.Caixa);
    }
  }, [hiddenTabs, tabIndex, setTabIndex]);

  const isLoading = useMemo(() => {
    return isLoadingBusinessAdmin || isLoadingEmployee || isLoadingBusiness;
  }, [isLoadingBusinessAdmin, isLoadingEmployee, isLoadingBusiness]);

  useEffect(() => {
    if (page !== undefined) {
      setTabIndex(page);
    }
  }, [page, setTabIndex]);

  useEffect(() => {
    const state = location.state as { page?: number; businessId?: number } | null;
    if (state?.page !== undefined) {
      const targetTab = state.page as BusinessTabsEnum;
      // Receptionist só pode navegar para tabs do RECEPTIONIST_TABS
      if (isReceptionist && !RECEPTIONIST_TABS.has(targetTab)) {
        setTabIndex(BusinessTabsEnum.Caixa);
      } else if (ALL_BUSINESS_TABS.includes(targetTab)) {
        setTabIndex(targetTab);
      }
    }
  }, [location.state, isReceptionist, setTabIndex]);

  // Seleciona o business específico vindo do onboarding
  useEffect(() => {
    if (!businessAdmins?.length) return;
    const state = location.state as { businessId?: number } | null;
    if (state?.businessId) {
      const admin = businessAdmins.find(ba => ba.businessId === state.businessId);
      if (admin) {
        setSelectedBusinessAdminId(admin.id);
      }
    }
  }, [businessAdmins, location.state]);

  const onChangeTab = useCallback(
    (_: React.SyntheticEvent, index: number) => {
      onVisualTabChange(_, index);
      const enumValue = renderedTabs[index];
      if (enumValue !== undefined) {
        navigate('/business', { state: { page: enumValue }, replace: true });
      }
    },
    [onVisualTabChange, renderedTabs, navigate]
  );

  // If onboarding never started, redirect to onboarding wizard (apenas admin)
  useEffect(() => {
    if (!isLoading && business && business.onboardingStep === 0 && !isReceptionist) {
      navigate('/onboarding', { state: { businessId: business.id }, replace: true });
    }
  }, [isLoading, business, navigate, isReceptionist]);

  // If business is blocked, show block screen instead of management UI
  if (!isLoading && business?.isBlocked) {
    return <BlockedScreen />;
  }

  return (isAuthenticated && (userRoles.includes(UserRoleEnum.BusinessAdmin) || isReceptionist)) ? (
    <LayoutShell>
      <LayoutRegister
        header={
          <Box>
            {/* Business selector: admin por BusinessAdmin, receptionist por BusinessEmployee */}
            {businessAdmins && businessAdmins.length > 1 && (
              <Box display='flex' alignItems='center' px={2} py={1}>
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel id='business-select-label'>Estabelecimento</InputLabel>
                  <Select
                    labelId='business-select-label'
                    value={businessAdmin?.id ?? ''}
                    label='Estabelecimento'
                    onChange={(e) => setSelectedBusinessAdminId(e.target.value as number)}
                  >
                    {businessAdmins.map((ba) => (
                      <MenuItem key={ba.id} value={ba.id}>
                        {ba.business?.name || `Business #${ba.businessId}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {isReceptionist && employee?.businessEmployees && employee.businessEmployees.length > 1 && (
              <Box display='flex' alignItems='center' px={2} py={1}>
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel id='bizemp-select-label'>Estabelecimento</InputLabel>
                  <Select
                    labelId='bizemp-select-label'
                    value={selectedBizEmp?.id ?? ''}
                    label='Estabelecimento'
                    onChange={(e) => setSelectedBizEmpId(e.target.value as number)}
                  >
                    {employee.businessEmployees.map((be) => (
                      <MenuItem key={be.id} value={be.id}>
                        {be.business?.name || `Business #${be.businessId}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Tabs value={visualIndex} onChange={onChangeTab} variant='scrollable' data-testid='business-tabs'>
              {renderedTabs.map((t) => {
                const config = BUSINESS_TAB_CONFIG[t];
                return (
                  <Tab key={t} label={config.label} icon={<Icon>{config.icon}</Icon>} iconPosition={lgDown ? 'top' : 'start'} data-testid={config.testId} />
                );
              })}
            </Tabs>
          </Box>
        }
        hideSaveButton
      >
        {isLoading && (
          <Box display='flex' justifyContent='center' alignItems='center'>
            <CircularProgress />
          </Box>
        )}

        {business && business.onboardingStep >= 1 && business.onboardingStep < 7 && !isReceptionist && (
          <OnboardingBanner business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Caixa && business && (
          <BusinessCaixa business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.OrderTickets && business && (
          <BusinessOrderTickets business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Clientes && business && (
          <BusinessClients business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.CashFlow && business && !hiddenTabs.has(BusinessTabsEnum.CashFlow) && (
          <BusinessCashFlow business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Transactions && business && (
          <BusinessTransactions business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Commissions && business && !hiddenTabs.has(BusinessTabsEnum.Commissions) && (
          <BusinessCommissions business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Dashboard && business && (
          <BusinessDashboard business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.WhatsApp && business && !hiddenTabs.has(BusinessTabsEnum.WhatsApp) && (
          <BusinessWhatsApp business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Information && business && !isReceptionist && (
          <BusinessInformation business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Services && business && !isReceptionist && (
          <BusinessServices business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Products && business && !isReceptionist && (
          <BusinessProducts business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Packages && business && !isReceptionist && (
          <BusinessPackages business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Employees && business && businessAdmin && !isReceptionist && (
          <BusinessEmployees business={business} refetch={refetchBusiness} businessAdminId={businessAdmin.id!} />
        )}

        {tabIndex === BusinessTabsEnum.Admins && business && businessAdmin && !isReceptionist && (
          <BusinessAdmins business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Goals && business && !hiddenTabs.has(BusinessTabsEnum.Goals) && (
          <BusinessGoals business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.FinancialSettings && business && !isReceptionist && (
          <BusinessFinancialSettings business={business} refetch={refetchBusiness} />
        )}

        {tabIndex === BusinessTabsEnum.Plan && business && !isReceptionist && (
          <MyPlan business={business} />
        )}

        {tabIndex === BusinessTabsEnum.Campaigns && business && !hiddenTabs.has(BusinessTabsEnum.Campaigns) && (
          <BusinessCampaigns business={business} refetch={refetchBusiness} />
        )}
      </LayoutRegister>
    </LayoutShell>
  ) : !isAuthenticated ? (
    <Box display='flex' justifyContent='center' alignItems='center' sx={{ minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  ) : (
    <Forbidden />
  );
};
